import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { useAuth } from "../../../../hooks";
import { NewReviTherapyApi, ReviEvaluationsApi } from "../../../../api/revi";
import { PERIODICIDADES, PERIODICIDAD_POR_DEFECTO } from "../periodicidad";

const therapyApi = new NewReviTherapyApi();
const evalApi = new ReviEvaluationsApi();

function getTherapistIdFromUser(user) {
  if (!user) return null;
  if (user.user?.therapists?.length > 0) return user.user.therapists[0];
  if (user.user?.therapist) return user.user.therapist;
  if (user.user?.therapistId) return user.user.therapistId;
  if (user.therapist) return user.therapist;
  if (user.therapistId) return user.therapistId;
  return null;
}

function filterByTitle(list, search) {
  const s = (search || "").trim().toLowerCase();
  if (!s) return list;
  return (list || []).filter((x) => (x?.title || "").toLowerCase().includes(s));
}

export function useAddSceneToTherapyWizard({
  isOpen,
  scene,
  onSuccess,
  onClose,
}) {
  // El hook no pinta nada, pero SI genera texto que ve el usuario: los avisos
  // de error y de exito. Por eso tambien se cablea.
  const { t } = useTranslation("rooms");

  const { accessToken, user } = useAuth();

  const therapistId = useMemo(() => getTherapistIdFromUser(user), [user]);

  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Step1 (therapies)
  const [therapiesLoading, setTherapiesLoading] = useState(false);
  const [therapiesRaw, setTherapiesRaw] = useState([]);
  const [selectedTherapyId, setSelectedTherapyId] = useState(null);
  const [therapySearch, setTherapySearch] = useState("");

  const therapies = useMemo(
    () => filterByTitle(therapiesRaw, therapySearch),
    [therapiesRaw, therapySearch],
  );

  // Step2
  const [description, setDescription] = useState("");

  // Step3 (evaluations)
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalTab, setEvalTab] = useState("standard"); // standard | my
  const [evalSearch, setEvalSearch] = useState("");

  const [standardRaw, setStandardRaw] = useState([]);
  const [myRaw, setMyRaw] = useState([]);

  const standardEvaluations = useMemo(
    () => filterByTitle(standardRaw, evalSearch),
    [standardRaw, evalSearch],
  );
  const myEvaluations = useMemo(
    () => filterByTitle(myRaw, evalSearch),
    [myRaw, evalSearch],
  );

  const [selectedEvalIds, setSelectedEvalIds] = useState([]);

  const toggleEvaluation = useCallback((evaluationId) => {
    setSelectedEvalIds((prev) => {
      if (prev.includes(evaluationId))
        return prev.filter((x) => x !== evaluationId);
      return [...prev, evaluationId];
    });
  }, []);

  // Preview modal (evaluation detail)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewEvaluation, setPreviewEvaluation] = useState(null);
  const previewReqIdRef = useRef(0);

  const openPreview = useCallback(
    async (ev) => {
      if (!ev?._id || !accessToken) return;
      setPreviewOpen(true);
      setPreviewLoading(true);

      const reqId = ++previewReqIdRef.current;
      try {
        const full = await evalApi.getEvaluationById(accessToken, ev._id);
        if (reqId !== previewReqIdRef.current) return;
        setPreviewEvaluation(full);
      } catch (e) {
        // `e.message` viene del backend y no se traduce aqui; lo que si es
        // nuestro es el texto de reserva.
        message.error(
          e.message || t("asistenteEscena.mensajes.errorCargarEvaluacion"),
        );
        setPreviewEvaluation(null);
      } finally {
        if (reqId === previewReqIdRef.current) setPreviewLoading(false);
      }
    },
    [accessToken, t],
  );

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewLoading(false);
    setPreviewEvaluation(null);
  }, []);

  // Step4
  const [periodicity, setPeriodicity] = useState(PERIODICIDAD_POR_DEFECTO);

  // Reset cuando abre/cambia escena
  useEffect(() => {
    if (!isOpen) return;
    setCurrentStep(0);
    setSelectedTherapyId(null);
    setTherapySearch("");
    setDescription("");
    setEvalTab("standard");
    setEvalSearch("");
    setSelectedEvalIds([]);
    setPeriodicity("Semanal");
    closePreview();
  }, [isOpen, scene?._id, closePreview]);

  // Load therapies (limit 50) cuando se abre
  useEffect(() => {
    if (!isOpen || !accessToken || !therapistId) return;

    const load = async () => {
      setTherapiesLoading(true);
      try {
        const res = await therapyApi.getMyTherapies(accessToken, therapistId, {
          page: 1,
          limit: 50,
          search: "",
        });
        setTherapiesRaw(res?.therapies || []);
      } catch (e) {
        setTherapiesRaw([]);
        message.error(
          e.message || t("asistenteEscena.mensajes.errorCargarTerapias"),
        );
      } finally {
        setTherapiesLoading(false);
      }
    };

    load();
  }, [isOpen, accessToken, therapistId, t]);

  // Load evaluations (standard + my) cuando se abre
  useEffect(() => {
    if (!isOpen || !accessToken || !therapistId) return;

    const load = async () => {
      setEvalLoading(true);
      try {
        const [standardRes, myRes] = await Promise.all([
          evalApi.getCatalog(accessToken, {
            page: 1,
            limit: 50,
            type: "standardized",
          }),
          evalApi.getMyEvaluations(accessToken, therapistId, {
            page: 1,
            limit: 50,
          }),
        ]);

        setStandardRaw(standardRes?.evaluations || []);
        setMyRaw(myRes?.evaluations || []);
      } catch (e) {
        setStandardRaw([]);
        setMyRaw([]);
        message.error(
          e.message || t("asistenteEscena.mensajes.errorCargarEvaluaciones"),
        );
      } finally {
        setEvalLoading(false);
      }
    };

    load();
  }, [isOpen, accessToken, therapistId, t]);

  const next = () => setCurrentStep((s) => Math.min(3, s + 1));
  const prev = () => setCurrentStep((s) => Math.max(0, s - 1));

  const canProceed = useCallback(() => {
    if (currentStep === 0) return !!selectedTherapyId;
    if (currentStep === 3) return !!periodicity;
    return true;
  }, [currentStep, selectedTherapyId, periodicity]);

  const handleSubmit = useCallback(async () => {
    if (!accessToken)
      return message.error(t("asistenteEscena.mensajes.sesionNoValida"));
    if (!scene?._id)
      return message.error(t("asistenteEscena.mensajes.escenaNoValida"));
    if (!selectedTherapyId)
      return message.error(t("asistenteEscena.mensajes.seleccionaTerapia"));

    const p =
      periodicity && PERIODICIDADES.includes(periodicity)
        ? periodicity
        : PERIODICIDAD_POR_DEFECTO;

    const payload = {
      virtualSceneId: scene._id,
      description: (description || "").trim(),
      periodicity: p,
      evaluations: selectedEvalIds,
    };

    setSubmitting(true);
    try {
      const therapy = await therapyApi.addSceneStep(
        accessToken,
        selectedTherapyId,
        payload,
      );

      message.success(t("asistenteEscena.mensajes.escenaAnadida"));
      onSuccess && onSuccess(therapy);
      onClose && onClose();
    } catch (e) {
      message.error(
        e.message || t("asistenteEscena.mensajes.errorAnadirEscena"),
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    accessToken,
    scene?._id,
    selectedTherapyId,
    description,
    periodicity,
    selectedEvalIds,
    onSuccess,
    onClose,
    t,
  ]);

  return {
    therapistId,

    currentStep,
    next,
    prev,
    canProceed,
    submitting,

    therapiesLoading,
    therapies,
    selectedTherapyId,
    setSelectedTherapyId,
    therapySearch,
    setTherapySearch,

    description,
    setDescription,

    evalTab,
    setEvalTab,
    evalSearch,
    setEvalSearch,
    evalLoading,
    standardEvaluations,
    myEvaluations,
    selectedEvalIds,
    toggleEvaluation,

    previewOpen,
    previewLoading,
    previewEvaluation,
    openPreview,
    closePreview,

    periodicity,
    setPeriodicity,

    handleSubmit,
  };
}
